import React, { useEffect, useState } from "react";
import { FaSearch, FaTrashAlt } from "react-icons/fa";
import { useAuth } from "./AuthContext";
import { useNavigate } from "react-router-dom";
import Spinner from "./Components/Spinner";

function SavedSearches() {
  const { user } = useAuth();
  const [searches, setSearches] = useState(null);
  const navigate = useNavigate();
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    fetch(`${process.env.REACT_APP_API_BASE}/savedsearches/${user.id}`)
      .then((res) => res.json())
      .then(setSearches)
      .catch(() => setSearches([]));
  }, [user]);

  const deleteSearch = async (id) => {
    if (deletingId === id) return;
    setDeletingId(id);
    try {
      await fetch(`${process.env.REACT_APP_API_BASE}/savedsearches/${id}`, { method: "DELETE" });
      setSearches((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      console.error("Failed to delete saved search:", err);
    } finally {
      setDeletingId(null);
    }
  };

  const runSearch = (search) => {
    sessionStorage.removeItem("cachedCars");
    sessionStorage.removeItem("cachedTotalPages");
    sessionStorage.removeItem("cachedTotalResults");
    sessionStorage.removeItem("cachedPage");
    sessionStorage.removeItem("cachedScroll");
    sessionStorage.removeItem("cachedQuery");

    window.gtag?.("event", "run_saved_search", { event_category: "engagement" });
    const query = new URLSearchParams();

    if (search.zip) query.set("zip", search.zip);
    if (search.makeName) query.set("make", search.makeName);
    if (search.model) query.set("model", search.model);
    if (search.minYear) query.set("minYear", search.minYear);
    if (search.maxYear) query.set("maxYear", search.maxYear);
    if (search.minPrice) query.set("minPrice", search.minPrice);
    if (search.maxPrice) query.set("maxPrice", search.maxPrice);
    if (search.maxDistance) query.set("radius", search.maxDistance);
    if (search.sort) query.set("sort", search.sort);

    navigate(`/search?${query.toString()}`);
  };

  if (!searches) return <Spinner />;

  return (
    <div className="bg-slate-50 px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto w-full max-w-7xl">
        <div className="mb-6 rounded-3xl border border-slate-200 bg-white px-5 py-5 shadow-sm sm:px-6">
          <div className="flex flex-col gap-2">
            <div className="inline-flex w-fit items-center rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-blue-700">
              Meshum dashboard
            </div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-950 sm:text-3xl">Your saved searches</h1>
            <p className="max-w-2xl text-sm leading-6 text-slate-600">
              Re-run the searches you care about, compare market changes, and return to the filters that matter.
            </p>
          </div>
        </div>

        {searches.length === 0 ? (
          <EmptySavedSearches onBrowse={() => navigate("/search")} />
        ) : (
          <div className="grid gap-4">
            {searches.map((search) => (
              <SavedSearchCard
                key={search.id}
                search={search}
                onRun={() => runSearch(search)}
                onDelete={() => deleteSearch(search.id)}
                deleting={deletingId === search.id}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SavedSearchCard({ search, onRun, onDelete, deleting }) {
  const criteria = [
    search.zip && { label: "ZIP", value: search.zip },
    search.makeName && { label: "Make", value: search.makeName },
    search.model && { label: "Model", value: search.model },
    search.maxDistance && { label: "Radius", value: `${search.maxDistance} mi` },
    search.minYear && { label: "Min year", value: search.minYear },
    search.maxYear && { label: "Max year", value: search.maxYear },
    search.minPrice && { label: "Min price", value: currency(search.minPrice) },
    search.maxPrice && { label: "Max price", value: currency(search.maxPrice) },
    search.minMileage && { label: "Min mileage", value: search.minMileage.toLocaleString() },
    search.maxMileage && { label: "Max mileage", value: search.maxMileage.toLocaleString() },
    search.sort && { label: "Sort", value: search.sort },
  ].filter(Boolean);

  return (
    <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <div className="text-lg font-semibold text-slate-950">{search.name || "Unnamed Search"}</div>
          <p className="mt-1 text-sm text-slate-500">Saved search criteria</p>

          <div className="mt-4 flex flex-wrap gap-2">
            {criteria.length > 0 ? (
              criteria.map((item) => (
                <div
                  key={`${item.label}-${item.value}`}
                  className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-700"
                >
                  <span className="text-slate-500">{item.label}:</span> {item.value}
                </div>
              ))
            ) : (
              <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-700">
                No criteria saved
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 md:justify-end">
          <button
            onClick={onRun}
            className="inline-flex h-11 items-center gap-2 rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
          >
            <FaSearch />
            Run search
          </button>
          <button
            onClick={onDelete}
            disabled={deleting}
            className="inline-flex h-11 items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 text-sm font-semibold text-rose-700 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FaTrashAlt />
            {deleting ? "Removing..." : "Delete"}
          </button>
        </div>
      </div>
    </article>
  );
}

function EmptySavedSearches({ onBrowse }) {
  return (
    <div className="mx-auto max-w-2xl rounded-3xl border border-dashed border-slate-300 bg-white px-6 py-10 text-center shadow-sm">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-blue-50 text-blue-700">
        <FaSearch className="text-xl" />
      </div>
      <h2 className="mt-4 text-xl font-semibold text-slate-950">No saved searches yet</h2>
      <p className="mt-2 text-sm leading-6 text-slate-600">
        Save a search from the results page so you can rerun it later and keep track of inventory changes.
      </p>
      <button
        onClick={onBrowse}
        className="mt-6 inline-flex h-11 items-center justify-center rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
      >
        Create a search
      </button>
    </div>
  );
}

function currency(n) {
  const value = Number(n);
  if (!Number.isFinite(value)) return "—";
  return value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

export default SavedSearches;
